<template>
  <HrPageLayout
    title="Imprest Request"
    subtitle="Create and manage imprest requests"
    icon="💰"
    :breadcrumb-items="[
      { label: 'Imprest' },
      { label: 'Request' }
    ]"
  >
    <div class="imprest-request-content">
      <!-- Stats -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-file"></i></div>
          <p class="hr-metric-label">Total Requests</p>
          <h2 class="hr-metric-value">{{ totalRequests }}</h2>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <p class="hr-metric-label">Pending Requests</p>
          <h2 class="hr-metric-value">{{ pendingRequests }}</h2>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-money"></i></div>
          <p class="hr-metric-label">Total Amount</p>
          <h2 class="hr-metric-value">{{ formatPrice(totalRequestAmount) }}</h2>
        </div>
      </div>

      <!-- Request Form -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title">
            <i class="bx me-2" :class="isEditing ? 'bx-edit-alt hr-text-warning' : 'bx-plus-circle hr-text-primary'"></i>
            {{ isEditing ? 'Edit Imprest Request' : 'Create New Imprest Request' }}
          </h5>
          <button v-if="isEditing" @click="cancelEdit" class="hr-btn hr-btn-sm hr-btn-outline-secondary">
            <i class="bx bx-x hr-me-1"></i>Cancel Edit
          </button>
        </div>
        <div class="hr-chart-body">
          <form @submit.prevent="submitImprestRequest">
            <div class="hr-form-grid hr-gap-4">
              <div class="hr-filter-group">
                <label class="hr-form-label">Purpose <span class="hr-text-danger">*</span></label>
                <input id="purpose" type="text" class="hr-input" v-model="imprestRequest.purpose" required placeholder="Enter purpose of imprest">
                <small class="hr-form-hint">Brief description of why you need this imprest</small>
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Amount <span class="hr-text-danger">*</span></label>
                <input id="amount" type="number" class="hr-input" v-model="imprestRequest.amount" required min="0" placeholder="Enter imprest amount">
                <small class="hr-form-hint">Amount requested in Kenyan Shillings (KES)</small>
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Department <span class="hr-text-danger">*</span></label>
                <select id="department" class="hr-select" v-model="imprestRequest.department" required>
                  <option value="" disabled>Select Department</option>
                  <option v-for="dept in departments" :key="dept.departmentID" :value="dept.departmentID">
                    {{ dept.departmentName }} - Available Budget: {{ dept.currency }} {{ formatPrice(dept.budget, dept.currency) }}
                  </option>
                </select>
                <small class="hr-form-hint">Select the department requesting the imprest</small>
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Expected Date of Expenditure <span class="hr-text-danger">*</span></label>
                <input id="expectedDate" type="date" class="hr-input" v-model="imprestRequest.expected_date" required :min="getTodayDate()">
                <small class="hr-form-hint">When do you expect to use these funds?</small>
              </div>
              <div class="hr-filter-group span-full">
                <label class="hr-form-label">Additional Details</label>
                <textarea id="details" class="hr-textarea" v-model="imprestRequest.details" rows="4" placeholder="Provide any additional details about this imprest request"></textarea>
                <small class="hr-form-hint">Any additional information that might support your request</small>
              </div>
            </div>

            <div v-if="alert.success || alert.info || alert.failed" class="hr-alert-wrap hr-mb-4">
              <div v-if="alert.success" class="hr-alert hr-alert-success hr-flex hr-flex-between">
                <span><i class="bx bx-check-circle hr-me-2"></i>{{ message }}</span>
                <button type="button" class="hr-btn hr-btn-sm" @click="clearAlerts">×</button>
              </div>
              <div v-if="alert.info" class="hr-alert hr-alert-info hr-flex hr-flex-between">
                <span><i class="bx bx-info-circle hr-me-2"></i>{{ message }}</span>
                <button type="button" class="hr-btn hr-btn-sm" @click="clearAlerts">×</button>
              </div>
              <div v-if="alert.failed" class="hr-alert hr-alert-danger hr-flex hr-flex-between">
                <span><i class="bx bx-error-circle hr-me-2"></i>{{ message }}</span>
                <button type="button" class="hr-btn hr-btn-sm" @click="clearAlerts">×</button>
              </div>
            </div>

            <div class="hr-flex hr-gap-2 hr-justify-end hr-mt-3">
              <button type="button" class="hr-btn hr-btn-secondary" @click="resetForm">
                <i class="bx bx-undo hr-me-1"></i>Reset
              </button>
              <button :disabled="disabled" type="submit" class="hr-btn hr-btn-primary">
                <i class="bx hr-me-1" :class="isEditing ? 'bx-save' : 'bx-send'"></i>{{ isEditing ? 'Update Request' : submitLoading }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Recent Requests -->
      <div class="hr-chart-card">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title"><i class="bx bx-history hr-me-2"></i>Recent Imprest Requests</h5>
          <button @click="getRecentRequests()" class="hr-btn hr-btn-sm hr-btn-primary">
            <i class="bx bx-refresh hr-me-1"></i>Refresh
          </button>
        </div>
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
              <tr>
                <th>ID</th>
                <th>Purpose</th>
                <th>Department</th>
                <th>Amount</th>
                <th>Expected Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-if="!recentRequests || recentRequests.length === 0">
                <td colspan="7" class="hr-text-center hr-py-4">
                  <i class="bx bx-file hr-me-2 hr-text-muted"></i>No recent imprest requests found
                </td>
              </tr>
              <tr v-for="(request, index) in recentRequests" :key="index">
                <td>{{ request.id || request.request_id }}</td>
                <td>{{ request.purpose }}</td>
                <td>{{ request.department }}</td>
                <td>{{ formatPrice(request.amount) }}</td>
                <td>{{ formatDate(request.expected_date) }}</td>
                <td><span :class="getStatusBadgeClass(request.status)">{{ request.status }}</span></td>
                <td>
                  <div class="hr-btn-group">
                    <button class="hr-btn hr-btn-sm hr-btn-outline-primary" title="View Details" @click="viewRequestDetails(request)">
                      <i class="bx bx-show"></i>
                    </button>
                    <button v-if="request.status === 'pending'" class="hr-btn hr-btn-sm hr-btn-outline-secondary" title="Edit Request" @click="editRequest(request)">
                      <i class="bx bx-edit"></i>
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Request Details Modal -->
    <div v-if="showDetailsModal" class="hr-modal-overlay" @click.self="showDetailsModal = false">
      <div class="hr-modal-dialog">
        <div class="hr-modal-content">
          <div class="hr-modal-header hr-flex-between">
            <h5 class="hr-modal-title"><i class="bx bx-file hr-me-2 hr-text-primary"></i>Imprest Request Details</h5>
            <button type="button" class="hr-modal-close" @click="showDetailsModal = false">×</button>
          </div>
          <div class="hr-modal-body" v-if="currentRequest">
            <div class="hr-mb-4 hr-text-center">
              <span :class="getStatusBadgeClass(currentRequest.status)" class="hr-badge-lg">{{ currentRequest.status.toUpperCase() }}</span>
            </div>
            <div class="hr-info-block hr-mb-3">
              <div class="hr-info-block-body">
                <div class="hr-info-row"><strong>Request ID:</strong><span>{{ currentRequest.request_id }}</span></div>
                <div class="hr-info-row"><strong>Purpose:</strong><span>{{ currentRequest.purpose }}</span></div>
                <div class="hr-info-row"><strong>Department:</strong><span>{{ currentRequest.department }}</span></div>
                <div class="hr-info-row"><strong>Amount:</strong><span>{{ formatPrice(currentRequest.amount) }}</span></div>
                <div class="hr-info-row"><strong>Expected Date:</strong><span>{{ formatDate(currentRequest.expected_date) }}</span></div>
              </div>
            </div>
            <div class="hr-mb-3">
              <strong class="hr-d-block hr-mb-2">Additional Details:</strong>
              <p class="hr-bg-light hr-p-3 hr-rounded hr-mb-0">{{ currentRequest.details || 'No additional details provided.' }}</p>
            </div>
            <div v-if="currentRequest.approvalDetails" class="hr-mb-3">
              <strong class="hr-d-block hr-mb-2">Approval Information:</strong>
              <div class="hr-bg-light hr-p-3 hr-rounded">
                <div v-if="currentRequest.approvalDetails.approvedBy" class="hr-flex hr-justify-between hr-mb-2">
                  <span>Approved By:</span><span>{{ currentRequest.approvalDetails.approvedBy }}</span>
                </div>
                <div v-if="currentRequest.approvalDetails.approvalDate" class="hr-flex hr-justify-between hr-mb-2">
                  <span>Approval Date:</span><span>{{ formatDate(currentRequest.approvalDetails.approvalDate) }}</span>
                </div>
                <div v-if="currentRequest.approvalDetails.comments" class="hr-mt-2">
                  <span class="hr-d-block">Comments:</span>
                  <p class="hr-mt-1 hr-mb-0">{{ currentRequest.approvalDetails.comments }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="hr-modal-footer hr-flex hr-gap-2 hr-justify-end">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showDetailsModal = false">Close</button>
            <button v-if="currentRequest && currentRequest.status === 'pending'" type="button" class="hr-btn hr-btn-primary" @click="editRequest(currentRequest); showDetailsModal = false;">
              <i class="bx bx-edit hr-me-1"></i>Edit Request
            </button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: 'ImprestRequest',
  components: { HrPageLayout },
  data() {
    return {
      alert: { success: false, info: false, failed: false },
      message: null,
      submitLoading: "Submit Request",
      loading: false,
      disabled: false,
      isEditing: false,
      editingRequestId: null,
      showDetailsModal: false,
      currentRequest: null,
      totalRequests: 12,
      pendingRequests: 3,
      totalRequestAmount: 250000,
      departments: [],
      imprestRequest: { purpose: '', amount: null, department: '', expected_date: '', details: '' },
      recentRequests: []
    };
  },
  mounted() {
    this.getDepartments();
    this.getRecentRequests();
  },
  methods: {
    formatPrice(value, currency = 'KES') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(value);
    },
    formatDate(dateString) {
      return dateString ? new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';
    },
    getTodayDate() {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    },
    getStatusBadgeClass(status) {
      const s = (status || '').toLowerCase();
      if (s === 'approved') return 'hr-badge hr-badge-success';
      if (s === 'pending') return 'hr-badge hr-badge-warning';
      if (s === 'rejected') return 'hr-badge hr-badge-danger';
      return 'hr-badge hr-badge-secondary';
    },
    clearAlerts() { this.alert.success = false; this.alert.info = false; this.alert.failed = false; this.message = null; },
    resetForm() {
      this.imprestRequest = { purpose: '', amount: null, department: '', expected_date: '', details: '' };
      this.clearAlerts();
      this.isEditing = false;
      this.editingRequestId = null;
    },
    editRequest(request) {
      this.isEditing = true;
      this.editingRequestId = request.request_id;
      this.imprestRequest = {
        purpose: request.purpose,
        amount: request.amount,
        department: this.getDepartmentIdByName(request.department),
        expected_date: request.expected_date,
        details: request.details || ''
      };
      this.$nextTick(() => {
        document.querySelector('.hr-chart-card')?.scrollIntoView({ behavior: 'smooth' });
      });
    },
    cancelEdit() {
      if (confirm('Are you sure you want to cancel editing? Any unsaved changes will be lost.')) this.resetForm();
    },
    getDepartmentIdByName(name) {
      const d = this.departments.find(dept => dept.departmentName === name);
      return d ? d.departmentID : '';
    },
    submitImprestRequest() {
      const isUpdate = this.isEditing;
      this.submitLoading = isUpdate ? "Updating..." : "Submitting...";
      this.message = isUpdate ? "Updating your imprest request..." : "Processing your imprest request...";
      this.alert.info = true;
      this.alert.success = false;
      this.alert.failed = false;
      this.disabled = true;
      const data = new FormData();
      if (isUpdate) { data.append("function", "updateImprestRequest"); data.append("requestId", this.editingRequestId); }
      else { data.append("function", "createImprestRequest"); }
      data.append("imprestRequest", JSON.stringify(this.imprestRequest));
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.submitLoading = "Submit Request";
          this.message = res.data.message || (isUpdate ? "Request updated successfully!" : "Request submitted successfully!");
          this.alert.success = true; this.alert.info = false; this.alert.failed = false;
          this.resetForm();
          this.isEditing = false;
          this.editingRequestId = null;
          this.getRecentRequests();
          setTimeout(() => this.clearAlerts(), 5000);
        } else {
          this.disabled = false;
          this.submitLoading = "Submit Request";
          this.message = res.data.message;
          this.alert.failed = true; this.alert.info = false; this.alert.success = false;
        }
      }).catch((e) => {
        this.disabled = false;
        this.submitLoading = "Submit Request";
        this.message = e.message || "An error occurred. Please try again.";
        this.alert.failed = true; this.alert.info = false; this.alert.success = false;
      });
    },
    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl).then((res) => {
        if (res.data.success) this.departments = res.data.data.departments || [];
        else { this.message = res.data.message; this.alert.failed = true; }
      }).catch((e) => { this.message = e.message || "Failed to load departments."; this.alert.failed = true; });
    },
    getRecentRequests() {
      const data = new FormData();
      data.append("function", "getRecentImprestRequests");
      execute(data, authUrl).then((res) => {
        if (res.data.success) this.recentRequests = res.data.data.recentRequests || [];
      }).catch(() => {});
    },
    viewRequestDetails(request) {
      this.currentRequest = { ...request };
      if (request.status === 'approved') {
        this.currentRequest.approvalDetails = { approvedBy: 'John Doe', approvalDate: '2025-03-12', comments: 'Request approved for the upcoming field activities.' };
      } else if (request.status === 'rejected') {
        this.currentRequest.approvalDetails = { approvedBy: 'Jane Smith', approvalDate: '2025-03-13', comments: 'Request rejected due to insufficient budget allocation.' };
      }
      this.showDetailsModal = true;
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hr-form-grid .span-full { grid-column: 1 / -1; }
.hr-alert-wrap .hr-alert { margin-bottom: 8px; }
.hr-alert-wrap .hr-alert:last-child { margin-bottom: 0; }
.hr-badge-lg { font-size: 1rem; padding: 0.5rem 1rem; }
.hr-bg-light { background: #f8fafc; }
.hr-rounded { border-radius: 12px; }
.hr-d-block { display: block; }
.hr-btn-group { display: flex; gap: 8px; }
.hr-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1050; }
.hr-modal-dialog { max-width: 500px; width: 90%; max-height: 90vh; overflow-y: auto; }
.hr-modal-content { background: #fff; border-radius: 16px; box-shadow: 0 10px 40px rgba(0,0,0,0.2); overflow: hidden; }
.hr-modal-header { padding: 20px; border-bottom: 1px solid #e2e8f0; }
.hr-modal-title { margin: 0; font-size: 1.1rem; }
.hr-modal-close { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #64748b; }
.hr-modal-body { padding: 20px; }
.hr-modal-footer { padding: 16px 20px; border-top: 1px solid #e2e8f0; }
</style>
